import { PropsWithChildren, Suspense, useEffect, useRef, useState } from 'react';
import { Outlet, Navigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store';
import Layout from '../components/layout/Layout';
import { PageFallback, RouteFallback } from './lazyRoute';
import axiosService from '../utils/axios';
import authSlice from '../store/slices/auth';
import { hasFinishedOnboarding } from '../pages/Onboarding/onboardingLogic';
import { safeReturnPath } from '../utils/authReturn';

type OnboardingGate = 'unknown' | 'needed' | 'done' | 'anonymous';

function loginRedirect(pathname: string, search: string, hash: string, location: ReturnType<typeof useLocation>) {
    const returnPath = safeReturnPath(`${pathname}${search}${hash}`);

    if (returnPath) {
        sessionStorage.setItem('postLoginRedirectPath', returnPath);
        localStorage.setItem('postLoginRedirectPath', returnPath);
    }

    const loginTo = returnPath ? `/login?redirect=${encodeURIComponent(returnPath)}` : '/login';
    return <Navigate to={loginTo} replace state={{ from: location }} />;
}

function useOnboardingGate(): OnboardingGate {
    const user = useSelector((state: RootState) => state.auth.user);
    const dispatch = useDispatch();
    const userRef = useRef(user);
    userRef.current = user;
    const [gate, setGate] = useState<OnboardingGate>(() => {
        if (!user) return 'anonymous';
        return hasFinishedOnboarding(user) ? 'done' : 'unknown';
    });

    useEffect(() => {
        const current = userRef.current;
        if (!current) {
            setGate('anonymous');
            return;
        }
        if (hasFinishedOnboarding(current)) {
            setGate('done');
            return;
        }

        let cancelled = false;
        setGate('unknown');
        axiosService.get('/user/onboarding')
            .then((response) => {
                const latest = userRef.current;
                if (cancelled || !latest) return;
                const serverFinished = response.data?.needsOnboarding === false || hasFinishedOnboarding(response.data);
                if (!serverFinished) {
                    setGate('needed');
                    return;
                }
                dispatch(authSlice.actions.setUser({
                    ...latest,
                    onboardingCompletedAt: response.data?.onboardingCompletedAt || latest.onboardingCompletedAt || new Date().toISOString(),
                    onboardingResumeStep: response.data?.onboardingResumeStep,
                    onboardingIdentity: response.data?.identity || response.data?.onboardingIdentity || latest.onboardingIdentity,
                }));
                setGate('done');
            })
            .catch(() => {
                if (!cancelled) setGate('needed');
            });
        return () => {
            cancelled = true;
        };
    }, [user?._id, user?.onboardingCompletedAt, dispatch]);

    return gate;
}

/**
 * Public pages such as someone else's profile. Logged-out visitors can see them.
 * A signed-in user who has not finished onboarding cannot.
 */
export function PublicProfileGate({ children }: PropsWithChildren) {
    const gate = useOnboardingGate();
    if (gate === 'anonymous' || gate === 'done') return children;
    if (gate === 'needed') return <Navigate to="/signup" replace />;
    return <PageFallback />;
}

const ProtectedRoute = () => {
    const auth = useSelector((state: RootState) => state.auth);
    const location = useLocation();
    const gate = useOnboardingGate();

    if (!auth.user) {
        return loginRedirect(location.pathname, location.search, location.hash, location);
    }

    if (gate === 'needed') {
        return <Navigate to="/signup" replace />;
    }

    if (gate !== 'done') {
        return <PageFallback />;
    }

    return (
        <Layout>
            {/* Route chunks load on demand; keep the header/side nav visible meanwhile. */}
            <Suspense fallback={<RouteFallback />}>
                <Outlet />
            </Suspense>
        </Layout>
    );
};

export default ProtectedRoute;
